// src/hooks/useBlogs.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { BlogService } from '../admin/pages/blogs/blogService';
import { BlogFormData } from '../admin/pages/blogs/blog';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────────────────────
export const BLOG_KEYS = {
  all: ['blogs'] as const,
  lists: () => ['blogs', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
    ['blogs', 'list', page, search, status] as const,
  stats: () => ['blogs', 'stats'] as const,
  commentCounts: (blogIds: string[]) =>
    ['blogs', 'commentCounts', ...blogIds.sort()] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
//
// FIX: Removed the `cursors` field — cursor-seek pagination only needs `page`.
// ─────────────────────────────────────────────────────────────────────────────
export interface BlogPaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// ─────────────────────────────────────────────────────────────────────────────
// useBlogsPaginated
//
// FIX: Passes `params.page` directly to getBlogsPaginated instead of cursor
// objects. Any page can now be fetched in any order.
// ─────────────────────────────────────────────────────────────────────────────
export const useBlogsPaginated = (params: BlogPaginationParams) => {
  return useQuery({
    queryKey: BLOG_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      BlogService.getBlogsPaginated(
        params.pageSize,
        params.page,
        params.status !== 'all' ? (params.status as 'draft' | 'published') : undefined,
        params.search
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBlogStats
// ─────────────────────────────────────────────────────────────────────────────
export const useBlogStats = () => {
  return useQuery({
    queryKey: BLOG_KEYS.stats(),
    queryFn: () => BlogService.getBlogStats(),
    staleTime: 1000 * 60 * 5,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBlogCommentCounts
// ─────────────────────────────────────────────────────────────────────────────
export const useBlogCommentCounts = (blogIds: string[]) => {
  return useQuery({
    queryKey: BLOG_KEYS.commentCounts(blogIds),
    queryFn: async (): Promise<Record<string, number>> => {
      if (blogIds.length === 0) return {};
      const counts: Record<string, number> = {};
      await Promise.all(
        blogIds.map(async (id) => {
          const commentsRef = collection(db, 'blogs', id, 'comments');
          const snapshot = await getDocs(query(commentsRef, orderBy('createdAt', 'asc')));
          counts[id] = snapshot.size;
        })
      );
      return counts;
    },
    enabled: blogIds.length > 0,
    staleTime: 1000 * 60 * 2,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useBlogMutations
// ─────────────────────────────────────────────────────────────────────────────
export const useBlogMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: BLOG_KEYS.all });
  };

  const create = useMutation({
    mutationFn: ({
      formData,
      userId,
      backgroundImage,
    }: {
      formData: BlogFormData;
      userId: string;
      backgroundImage?: File | null;
    }) => BlogService.createBlog(formData, userId, backgroundImage),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({
      blogId,
      formData,
      backgroundImage,
      removeImage,
    }: {
      blogId: string;
      formData: BlogFormData;
      backgroundImage?: File | null;
      removeImage?: boolean;
    }) => BlogService.updateBlog(blogId, formData, backgroundImage, removeImage),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (blogId: string) => BlogService.deleteBlog(blogId),
    onSuccess: invalidateAll,
  });

  const toggleStatus = useMutation({
    mutationFn: ({
      blogId,
      status,
    }: {
      blogId: string;
      status: 'draft' | 'published';
    }) => BlogService.toggleBlogStatus(blogId, status),
    onSuccess: invalidateAll,
  });

  return { create, update, remove, toggleStatus };
};